import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import Navbar from '../components/sections/Navbar';
import Footer from '../components/sections/Footer';
import { Send, Phone, MapPin, Mail, CheckCircle2, ArrowLeft, Clock } from 'lucide-react';
import axios from 'axios';
import { axiosErrorMessage } from '../lib/errors';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function ContactPage() {
  const navigate = useNavigate();
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.05 });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({
    name: '',
    company: '',
    email: '',
    phone: '',
    project_type: '',
    message: '',
    honeypot: '',
  });

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.honeypot) return;
    setLoading(true);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/contact`, form);
      if (response.data.success) {
        setSubmitted(true);
        toast.success('Message sent successfully!', {
          description: 'We\'ll get back to you shortly.',
        });
      }
    } catch (error) {
      const msg = axiosErrorMessage(error, 'Something went wrong. Please try again.');
      toast.error('Failed to send message', { description: msg });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main id="main-content" tabIndex={-1}>

      {/* Hero */}
      <section className="relative py-20 sm:py-28 bg-coal text-white overflow-hidden" data-testid="contact-hero">
        <div className="noise-overlay" />
        <div className="max-w-6xl mx-auto px-4 sm:px-6 relative z-10">
          <Button
            variant="ghost"
            onClick={() => navigate('/')}
            className="text-white/60 hover:text-white hover:bg-white/10 mb-6 -ml-2 gap-2"
            data-testid="contact-back-button"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Button>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <h1 className="editorial-headline text-4xl sm:text-5xl lg:text-6xl text-white leading-[1.05] mb-6">
              Get in <span className="italic font-normal text-forest-2">touch.</span>
            </h1>
            <p className="text-lg sm:text-xl text-white/70 max-w-2xl leading-relaxed">
              Contact us about your project needs. We specialize in concrete foundations for all water, storage, and industrial tanks.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact content */}
      <section className="py-16 sm:py-20 lg:py-28 bg-background" ref={ref} data-testid="contact-section">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
            {/* Left: Contact info */}
            <motion.div
              className="lg:col-span-5"
              initial={{ opacity: 0, y: 12 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5 }}
            >
              <p className="font-mono text-xs tracking-widest uppercase text-muted-foreground mb-3">Contact Information</p>
              <h2 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight text-foreground mb-6">
                We look forward to hearing from you.
              </h2>
              <p className="text-muted-foreground leading-relaxed mb-8">
                Whether you have a new project to discuss or questions about our work, our Conway team is ready to help.
              </p>

              <div className="space-y-6">
                <div className="flex items-start gap-4 p-4 rounded-xl bg-card border border-border">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Phone className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <p className="font-display font-semibold text-sm text-foreground">Phone</p>
                    <a href="tel:5014507538" className="text-sm text-muted-foreground hover:text-primary transition-colors">
                      (501) 450-7538
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4 p-4 rounded-xl bg-card border border-border">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <p className="font-display font-semibold text-sm text-foreground">Address</p>
                    <p className="text-sm text-muted-foreground">1002 Artis Ln<br />Conway, AR 72034</p>
                  </div>
                </div>

                <div className="flex items-start gap-4 p-4 rounded-xl bg-card border border-border">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Clock className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <p className="font-display font-semibold text-sm text-foreground">Business Hours</p>
                    <p className="text-sm text-muted-foreground">Monday - Friday: 7:00 AM - 5:00 PM</p>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Right: Contact form */}
            <motion.div
              className="lg:col-span-7"
              initial={{ opacity: 0, y: 12 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: 0.15 }}
            >
              <Card className="relative overflow-hidden">
                <div className="spec-strip" />
                <CardContent className="p-6 sm:p-8 pt-10">
                  {submitted ? (
                    <div className="text-center py-12">
                      <CheckCircle2 className="w-16 h-16 text-primary mx-auto mb-4" />
                      <h3 className="font-display font-semibold text-2xl text-foreground mb-2">Message Sent!</h3>
                      <p className="text-muted-foreground mb-6">Thank you for reaching out. We'll get back to you shortly.</p>
                      <Button onClick={() => { setSubmitted(false); setForm({ name: '', company: '', email: '', phone: '', project_type: '', message: '', honeypot: '' }); }}>
                        Send Another Message
                      </Button>
                    </div>
                  ) : (
                    <form onSubmit={handleSubmit} className="space-y-5">
                      {/* Honeypot */}
                      <div className="hidden">
                        <Input value={form.honeypot} onChange={(e) => handleChange('honeypot', e.target.value)} tabIndex={-1} autoComplete="off" />
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="contact-name" className="text-sm font-medium">Name *</Label>
                          <Input
                            id="contact-name"
                            value={form.name}
                            onChange={(e) => handleChange('name', e.target.value)}
                            required
                            placeholder="Your full name"
                            className="mt-1.5"
                            data-testid="contact-form-name"
                          />
                        </div>
                        <div>
                          <Label htmlFor="contact-company" className="text-sm font-medium">Company</Label>
                          <Input
                            id="contact-company"
                            value={form.company}
                            onChange={(e) => handleChange('company', e.target.value)}
                            placeholder="Company name"
                            className="mt-1.5"
                            data-testid="contact-form-company"
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="contact-email" className="text-sm font-medium">Email *</Label>
                          <Input
                            id="contact-email"
                            type="email"
                            value={form.email}
                            onChange={(e) => handleChange('email', e.target.value)}
                            required
                            placeholder="your@email.com"
                            className="mt-1.5"
                            data-testid="contact-form-email"
                          />
                        </div>
                        <div>
                          <Label htmlFor="contact-phone" className="text-sm font-medium">Phone *</Label>
                          <Input
                            id="contact-phone"
                            type="tel"
                            value={form.phone}
                            onChange={(e) => handleChange('phone', e.target.value)}
                            required
                            placeholder="(555) 555-5555"
                            className="mt-1.5"
                            data-testid="contact-form-phone"
                          />
                        </div>
                      </div>

                      <div>
                        <Label htmlFor="contact-project-type" className="text-sm font-medium">Project Type</Label>
                        <Select value={form.project_type} onValueChange={(val) => handleChange('project_type', val)}>
                          <SelectTrigger id="contact-project-type" aria-label="Project type" className="mt-1.5" data-testid="contact-form-project-type">
                            <SelectValue placeholder="Select project type" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="concrete-foundation">Concrete Foundation</SelectItem>
                            <SelectItem value="elevated-foundation">Elevated Foundation</SelectItem>
                            <SelectItem value="mechanical-piping">Mechanical Piping</SelectItem>
                            <SelectItem value="concrete-vault">Concrete Vault</SelectItem>
                            <SelectItem value="site-work">Site Grading & Clearing</SelectItem>
                            <SelectItem value="road-construction">Road Construction</SelectItem>
                            <SelectItem value="erosion-control">Erosion Control</SelectItem>
                            <SelectItem value="other">Other</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div>
                        <Label htmlFor="contact-message" className="text-sm font-medium">Message</Label>
                        <Textarea
                          id="contact-message"
                          value={form.message}
                          onChange={(e) => handleChange('message', e.target.value)}
                          placeholder="Tell us about your project..."
                          rows={5}
                          className="mt-1.5 resize-none"
                          data-testid="contact-form-message"
                        />
                      </div>

                      <Button
                        type="submit"
                        className="w-full btn-lift gap-2 py-6"
                        disabled={loading}
                        data-testid="contact-form-submit-button"
                      >
                        {loading ? (
                          <span className="flex items-center gap-2">
                            <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>
                            Sending...
                          </span>
                        ) : (
                          <>
                            <Send className="w-4 h-4" />
                            Send Message
                          </>
                        )}
                      </Button>
                    </form>
                  )}
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>

      </main>
      <Footer />
    </div>
  );
}
